import { gql } from '@apollo/client';

// Query to get all available potential investors
export const GET_ALL_POTENTIAL_INVESTORS = gql`
  query GetAllPotentialInvestors {
    potentialInvestors {
      edges {
        node {
          id
          name
          isActive
        }
      }
    }
  }
`;

// GraphQL Query
export const GET_CONTACT_SUMMARY = gql`
  query GetContactSummary($id: ID!) {
    node(id: $id) {
      ... on ContactNode {
        id
        entityImageId
        createDate
        writeDate
        createUid {
          id
          name
        }
        writeUid {
          id
          name
        }
        createdByDelegate {
          id
          name
        }
        modifiedByDelegate {
          id
          name
        }
        exchangeRate
        currencyId
        firstLastStreet
        timeZoneRuleVersionNumber
        versionNumber
        name
        firstName
        lastName
        middleName
        nickname
        salutation
        suffix
        yomiFullName
        gender
        maritalStatus
        birthday
        anniversary
        spousePartnerName
        image
        isMinor
        isMinorWithParentalConsent
        image128
        email
        phone
        mobile
        emailAddress2
        emailAddress3
        emailStatus
        statusDate
        companyPhone
        assistantPhone
        ftpSite
        portalUrl
        crmUrl
        linkedin
        website
        companyName {
          __typename
          ... on AccountNode {
            id
            name
          }
          ... on ContactNode {
            id
            name
          }
        }
        function
        jobTitle
        department
        newCompanyName
        isAssistant
        assistantName
        assistantId {
          id
          name
        }
        managingPartnerId {
          id
          name
        }
        notAtCompanyFlag
        ownerId {
          id
          name
        }
        secondaryOwnerId {
          id
          name
        }
        smOwnerOriginal
        secondarySmOwnerOriginal
        referredBy {
          id
          name
        }
        parentId {
          id
          name
        }
        parentContactid
        relationshipType
        placementAgent
        placementAgentDateAssigned
        metAtThrough
        relationshipPriority
        relationshipStrength
        relationshipStrengthDescription
        allowTextMessages
        followEmailActivity
        doNotAllowBulkEmails
        gdprOptout
        gdprSuppress
        suppressCmlDripEmails
        temporarilySuppressAllEmails
        reasonUncheckedTempSuppress
        dateTempSuppressWasUnchecked
        disableWebTracking
        receiveInvestorCommunications
        unsubscribedBy
        unsubscribedDate
        currentInvestor
        accreditedInvestor
        qualifiedPurchaser
        bdc
        evergreenCreditFund
        estimatedProbabilityClosing
        likelyInvestmentAmount
        starMountainAnnualShareholderMeeting
        investorType
        potentialInvestorPriority
        serviceProviderType
        serviceProviderFlag
        militaryBranch
        militaryAffiliation
        militaryDuty
        description
        generalNotes
        biography
        shortBiography
        summaryNotes
        pmiNotes
        nextSteps
        totalActivities
        totalNotes
        lastActivityAddedOn
        lastNoteAddedOn
        lastSynchedToNotion
        legacyTotalScore
        identifiedOn
        lastCompletedStep
        contactRequiresReview
        rmOutlookSync
        requestedDoNotSendGifts
        autoCreated
        owningUser {
          id
          name
        }
        ny2QaCheckedBy {
          id
          name
        }
        ny2QaCheckNeeded
        street
        street2
        city
        stateId
        zip
        countryId
        cityOriginal
        stateOriginal
        countryOriginal
        opstreamAccountId {
          id
          name
        }
        potentialInvestors {
          id
          name
        }
        dynamicsId
        ilevelId
        notionPageId
      }
    }
  }
`;

/** Reference to either an Account or a Contact (company_name field) */
export interface CompanyNameReference {
  __typename: 'AccountNode' | 'ContactNode';
  id: string;
  name: string;
}

// UI Data Types
export interface ContactActivityTracking {
  lastActivityAddedOn: string;
  lastNoteAddedOn: string;
  lastSynchedToNotion: string;
  legacyTotalScore: null | number;
  totalActivities: null | number;
  totalNotes: null | number;
}

export interface ContactCommunicationPreferences {
  allowTextMessages: boolean;
  dateTempSuppressUnchecked: string;
  disableWebTracking: boolean;
  doNotAllowBulkEmails: boolean;
  followEmailActivity: boolean;
  gdprOptout: boolean;
  gdprSuppress: boolean;
  reasonForUncheckedTempSuppress: string;
  receiveInvestorCommunications: boolean;
  suppressCmlDripEmails: boolean;
  temporarilySuppressAllEmails: boolean;
  unsubscribeDate: string;
  unsubscribedBy: string;
}

export interface ContactInfo {
  assistantPhone: string;
  companyPhone: string;
  emailAddress2: string;
  emailAddress3: string;
  emailStatus: string;
  ftpSite: string;
  mobilePhone: string;
  portalUrl: string;
  primaryEmail: string;
  website: string;
}

export interface ContactInvestmentInfo {
  accreditedInvestor: boolean;
  bdc: boolean;
  currentInvestor: boolean;
  estimatedProbabilityOfClosing: string;
  likelyInvestmentAmount: string;
  qualifiedPurchaser: boolean;
  starMountainAnnualShareholderMeeting: boolean;
}

export interface ContactMilitaryInfo {
  militaryAffiliation: string;
  militaryDuty: string;
}

export interface ContactNode {
  accreditedInvestor?: boolean | null;
  allowTextMessages?: boolean | null;
  anniversary?: null | string;
  assistantId?: ContactNodeLookup | null;
  assistantName?: null | string;
  assistantPhone?: null | string;
  autoCreated?: boolean | null;
  bdc?: boolean | null;
  biography?: null | string;
  birthday?: null | string;
  city?: null | string;
  cityOriginal?: null | string;
  companyName?: CompanyNameReference | null;
  companyPhone?: null | string;
  contactRequiresReview?: boolean | null;
  countryId?: null | string;
  countryOriginal?: null | string;
  createDate?: null | string;
  createdByDelegate?: ContactNodeLookup | null;
  createUid?: ContactNodeLookup | null;
  crmUrl?: null | string;
  currencyId?: null | string;
  currentInvestor?: boolean | null;
  dateTempSuppressWasUnchecked?: null | string;
  department?: null | string;
  description?: null | string;
  disableWebTracking?: boolean | null;
  doNotAllowBulkEmails?: boolean | null;
  dynamicsId?: null | string;
  email?: null | string;
  emailAddress2?: null | string;
  emailAddress3?: null | string;
  emailStatus?: null | string;
  entityImageId?: null | string;
  estimatedProbabilityClosing?: null | string;
  evergreenCreditFund?: boolean | null;
  exchangeRate?: null | number;
  firstLastStreet?: null | string;
  firstName?: null | string;
  followEmailActivity?: boolean | null;
  ftpSite?: null | string;
  function?: null | string;
  gdprOptout?: boolean | null;
  gdprSuppress?: boolean | null;
  gender?: null | string;
  generalNotes?: null | string;
  id: string;
  identifiedOn?: null | string;
  ilevelId?: null | string;
  image?: null | string;
  image128?: null | string;
  investorType?: null | string;
  isAssistant?: boolean | null;
  isMinor?: boolean | null;
  isMinorWithParentalConsent?: boolean | null;
  jobTitle?: null | string;
  lastActivityAddedOn?: null | string;
  lastCompletedStep?: null | string;
  lastName?: null | string;
  lastNoteAddedOn?: null | string;
  lastSynchedToNotion?: null | string;
  legacyTotalScore?: null | number;
  likelyInvestmentAmount?: null | string;
  linkedin?: null | string;
  managingPartnerId?: ContactNodeLookup | null;
  maritalStatus?: null | string;
  metAtThrough?: null | string;
  middleName?: null | string;
  militaryAffiliation?: null | string;
  militaryBranch?: null | string;
  militaryDuty?: null | string;
  mobile?: null | string;
  modifiedByDelegate?: ContactNodeLookup | null;
  name: string;
  newCompanyName?: null | string;
  nextSteps?: null | string;
  nickname?: null | string;
  notAtCompanyFlag?: null | string;
  notionPageId?: null | string;
  ny2QaCheckedBy?: ContactNodeLookup | null;
  ny2QaCheckNeeded?: boolean | null;
  opstreamAccountId?: ContactNodeLookup | null;
  ownerId?: ContactNodeLookup | null;
  owningUser?: ContactNodeLookup | null;
  parentContactid?: null | string;
  parentId?: ContactNodeLookup | null;
  phone?: null | string;
  placementAgent?: null | string;
  placementAgentDateAssigned?: null | string;
  pmiNotes?: null | string;
  portalUrl?: null | string;
  potentialInvestorPriority?: null | string;
  potentialInvestors?: ContactNodeLookup[] | null;
  qualifiedPurchaser?: boolean | null;
  reasonUncheckedTempSuppress?: null | string;
  receiveInvestorCommunications?: boolean | null;
  referredBy?: ContactNodeLookup | null;
  relationshipPriority?: null | string;
  relationshipStrength?: null | string;
  relationshipStrengthDescription?: null | string;
  relationshipType?: null | string;
  requestedDoNotSendGifts?: boolean | null;
  rmOutlookSync?: boolean | null;
  salutation?: null | string;
  secondaryOwnerId?: ContactNodeLookup | null;
  secondarySmOwnerOriginal?: null | string;
  serviceProviderFlag?: boolean | null;
  serviceProviderType?: null | string;
  shortBiography?: null | string;
  smOwnerOriginal?: null | string;
  spousePartnerName?: null | string;
  starMountainAnnualShareholderMeeting?: boolean | null;
  stateId?: null | string;
  stateOriginal?: null | string;
  statusDate?: null | string;
  street?: null | string;
  street2?: null | string;
  suffix?: null | string;
  summaryNotes?: null | string;
  suppressCmlDripEmails?: boolean | null;
  temporarilySuppressAllEmails?: boolean | null;
  timeZoneRuleVersionNumber?: null | number;
  totalActivities?: null | number;
  totalNotes?: null | number;
  unsubscribedBy?: null | string;
  unsubscribedDate?: null | string;
  versionNumber?: null | number;
  website?: null | string;
  writeDate?: null | string;
  writeUid?: ContactNodeLookup | null;
  yomiFullName?: null | string;
  zip?: null | string;
}

// API Response Types
export interface ContactNodeLookup {
  id: string;
  name: string;
}

export interface ContactNotes {
  biography: string;
  description: string;
  pmiNotes: string;
  shortBiography: string;
  summaryNotes: string;
}

export interface ContactPersonalInfo {
  anniversary: string;
  birthday: string;
  companyName: string;
  companyNameId: string;
  firstName: string;
  gender: string;
  image: string;
  isMinor: boolean;
  isMinorWithParentalConsent: boolean;
  lastName: string;
  maritalStatus: string;
  middleName: string;
  nickname: string;
  salutation: string;
  spousePartnerName: string;
  suffix: string;
  website: string;
}

export interface ContactRelationshipInfo {
  metAtOrMetThrough: string;
  owner: string;
  ownerId: string;
  parentContact: string;
  parentContactId: string;
  placementAgent: string;
  placementAgentDateAssigned: string;
  referredBy: string;
  referredById: string;
  relationshipType: string;
  secondaryOwner: string;
  secondaryOwnerId: string;
}

export interface ContactReviewStatus {
  contactRequiresReview: boolean;
  identifiedOn: string;
  lastCompletedStep: string;
  nextSteps: string;
  requestedDoNotSendGifts: boolean;
  rmOutlookSync: boolean;
  statusDate: string;
}

export interface ContactSummaryData {
  activityTracking: ContactActivityTracking;
  communicationPreferences: ContactCommunicationPreferences;
  contactInfo: ContactInfo;
  investmentInfo: ContactInvestmentInfo;
  militaryInfo: ContactMilitaryInfo;
  notes: ContactNotes;
  personalInfo: ContactPersonalInfo;
  potentialInvestors: ContactNodeLookup[];
  relationshipInfo: ContactRelationshipInfo;
  reviewStatus: ContactReviewStatus;
  systemMetadata: ContactSystemMetadata;
  workInfo: ContactWorkInfo;
}

export interface ContactSystemMetadata {
  createdBy: string;
  createdByDelegate: string;
  createdOn: string;
  currency: string;
  entityImageId: string;
  exchangeRate: null | number;
  firstLastStreet: string;
  modifiedBy: string;
  modifiedByDelegate: string;
  modifiedOn: string;
  timeZoneRuleVersionNumber: null | number;
}

export interface ContactWorkInfo {
  assistant: string;
  assistantId: string;
  companyName: string;
  department: string;
  isAssistant: boolean;
  jobTitle: string;
  managingPartner: string;
  managingPartnerId: string;
  newCompanyName: string;
  notAtCompanyFlag: string;
}

// Helper functions
export function createEmptyContactSummaryData(): ContactSummaryData {
  return {
    activityTracking: {
      lastActivityAddedOn: '',
      lastNoteAddedOn: '',
      lastSynchedToNotion: '',
      legacyTotalScore: null,
      totalActivities: null,
      totalNotes: null
    },
    communicationPreferences: {
      allowTextMessages: false,
      dateTempSuppressUnchecked: '',
      disableWebTracking: false,
      doNotAllowBulkEmails: false,
      followEmailActivity: false,
      gdprOptout: false,
      gdprSuppress: false,
      reasonForUncheckedTempSuppress: '',
      receiveInvestorCommunications: false,
      suppressCmlDripEmails: false,
      temporarilySuppressAllEmails: false,
      unsubscribeDate: '',
      unsubscribedBy: ''
    },
    contactInfo: {
      assistantPhone: '',
      companyPhone: '',
      emailAddress2: '',
      emailAddress3: '',
      emailStatus: '',
      ftpSite: '',
      mobilePhone: '',
      portalUrl: '',
      primaryEmail: '',
      website: ''
    },
    investmentInfo: {
      accreditedInvestor: false,
      bdc: false,
      currentInvestor: false,
      estimatedProbabilityOfClosing: '',
      likelyInvestmentAmount: '',
      qualifiedPurchaser: false,
      starMountainAnnualShareholderMeeting: false
    },
    militaryInfo: {
      militaryAffiliation: '',
      militaryDuty: ''
    },
    notes: {
      biography: '',
      description: '',
      pmiNotes: '',
      shortBiography: '',
      summaryNotes: ''
    },
    personalInfo: {
      anniversary: '',
      birthday: '',
      companyName: '',
      companyNameId: '',
      firstName: '',
      gender: '',
      image: '',
      isMinor: false,
      isMinorWithParentalConsent: false,
      lastName: '',
      maritalStatus: '',
      middleName: '',
      nickname: '',
      salutation: '',
      spousePartnerName: '',
      suffix: '',
      website: ''
    },
    potentialInvestors: [],
    relationshipInfo: {
      metAtOrMetThrough: '',
      owner: '',
      ownerId: '',
      parentContact: '',
      parentContactId: '',
      placementAgent: '',
      placementAgentDateAssigned: '',
      referredBy: '',
      referredById: '',
      relationshipType: '',
      secondaryOwner: '',
      secondaryOwnerId: ''
    },
    reviewStatus: {
      contactRequiresReview: false,
      identifiedOn: '',
      lastCompletedStep: '',
      nextSteps: '',
      requestedDoNotSendGifts: false,
      rmOutlookSync: false,
      statusDate: ''
    },
    systemMetadata: {
      createdBy: '',
      createdByDelegate: '',
      createdOn: '',
      currency: '',
      entityImageId: '',
      exchangeRate: null,
      firstLastStreet: '',
      modifiedBy: '',
      modifiedByDelegate: '',
      modifiedOn: '',
      timeZoneRuleVersionNumber: null
    },
    workInfo: {
      assistant: '',
      assistantId: '',
      companyName: '',
      department: '',
      isAssistant: false,
      jobTitle: '',
      managingPartner: '',
      managingPartnerId: '',
      newCompanyName: '',
      notAtCompanyFlag: ''
    }
  };
}

export function mapContactSummary(node: ContactNode): ContactSummaryData {
  return {
    activityTracking: {
      lastActivityAddedOn: node.lastActivityAddedOn ?? '',
      lastNoteAddedOn: node.lastNoteAddedOn ?? '',
      lastSynchedToNotion: node.lastSynchedToNotion ?? '',
      legacyTotalScore: node.legacyTotalScore ?? null,
      totalActivities: node.totalActivities ?? null,
      totalNotes: node.totalNotes ?? null
    },
    communicationPreferences: {
      allowTextMessages: node.allowTextMessages ?? false,
      dateTempSuppressUnchecked: node.dateTempSuppressWasUnchecked ?? '',
      disableWebTracking: node.disableWebTracking ?? false,
      doNotAllowBulkEmails: node.doNotAllowBulkEmails ?? false,
      followEmailActivity: node.followEmailActivity ?? false,
      gdprOptout: node.gdprOptout ?? false,
      gdprSuppress: node.gdprSuppress ?? false,
      reasonForUncheckedTempSuppress: node.reasonUncheckedTempSuppress ?? '',
      receiveInvestorCommunications:
        node.receiveInvestorCommunications ?? false,
      suppressCmlDripEmails: node.suppressCmlDripEmails ?? false,
      temporarilySuppressAllEmails: node.temporarilySuppressAllEmails ?? false,
      unsubscribeDate: node.unsubscribedDate ?? '',
      unsubscribedBy: node.unsubscribedBy ?? ''
    },
    contactInfo: {
      assistantPhone: node.assistantPhone ?? '',
      companyPhone: node.companyPhone ?? '',
      emailAddress2: node.emailAddress2 ?? '',
      emailAddress3: node.emailAddress3 ?? '',
      emailStatus: node.emailStatus ?? '',
      ftpSite: node.ftpSite ?? '',
      mobilePhone: node.mobile ?? '',
      portalUrl: node.portalUrl ?? '',
      primaryEmail: node.email ?? '',
      website: node.website ?? ''
    },
    investmentInfo: {
      accreditedInvestor: node.accreditedInvestor ?? false,
      bdc: node.bdc ?? false,
      currentInvestor: node.currentInvestor ?? false,
      estimatedProbabilityOfClosing: node.estimatedProbabilityClosing ?? '',
      likelyInvestmentAmount: node.likelyInvestmentAmount ?? '',
      qualifiedPurchaser: node.qualifiedPurchaser ?? false,
      starMountainAnnualShareholderMeeting:
        node.starMountainAnnualShareholderMeeting ?? false
    },
    militaryInfo: {
      militaryAffiliation: node.militaryAffiliation ?? '',
      militaryDuty: node.militaryDuty ?? ''
    },
    notes: {
      biography: node.biography ?? '',
      description: node.description ?? '',
      pmiNotes: node.pmiNotes ?? '',
      shortBiography: node.shortBiography ?? '',
      summaryNotes: node.summaryNotes ?? ''
    },
    personalInfo: {
      anniversary: node.anniversary ?? '',
      birthday: node.birthday ?? '',
      companyName: node.companyName?.name ?? '',
      companyNameId: node.companyName?.id ?? '',
      firstName: node.firstName ?? '',
      gender: node.gender ?? '',
      image: node.image ?? '',
      isMinor: node.isMinor ?? false,
      isMinorWithParentalConsent: node.isMinorWithParentalConsent ?? false,
      lastName: node.lastName ?? '',
      maritalStatus: node.maritalStatus ?? '',
      middleName: node.middleName ?? '',
      nickname: node.nickname ?? '',
      salutation: node.salutation ?? '',
      spousePartnerName: node.spousePartnerName ?? '',
      suffix: node.suffix ?? '',
      website: node.website ?? ''
    },
    potentialInvestors: node.potentialInvestors ?? [],
    relationshipInfo: {
      metAtOrMetThrough: node.metAtThrough ?? '',
      owner: node.ownerId?.name ?? '',
      ownerId: node.ownerId?.id ?? '',
      parentContact: node.parentId?.name ?? '',
      parentContactId: node.parentId?.id ?? '',
      placementAgent: node.placementAgent ?? '',
      placementAgentDateAssigned: node.placementAgentDateAssigned ?? '',
      referredBy: node.referredBy?.name ?? '',
      referredById: node.referredBy?.id ?? '',
      relationshipType: node.relationshipType ?? '',
      secondaryOwner: node.secondaryOwnerId?.name ?? '',
      secondaryOwnerId: node.secondaryOwnerId?.id ?? ''
    },
    reviewStatus: {
      contactRequiresReview: node.contactRequiresReview ?? false,
      identifiedOn: node.identifiedOn ?? '',
      lastCompletedStep: node.lastCompletedStep ?? '',
      nextSteps: node.nextSteps ?? '',
      requestedDoNotSendGifts: node.requestedDoNotSendGifts ?? false,
      rmOutlookSync: node.rmOutlookSync ?? false,
      statusDate: node.statusDate ?? ''
    },
    systemMetadata: {
      createdBy: node.createUid?.name ?? '',
      createdByDelegate: node.createdByDelegate?.name ?? '',
      createdOn: node.createDate ?? '',
      currency: node.currencyId ?? '',
      entityImageId: node.entityImageId ?? '',
      exchangeRate: node.exchangeRate ?? null,
      firstLastStreet: node.firstLastStreet ?? '',
      modifiedBy: node.writeUid?.name ?? '',
      modifiedByDelegate: node.modifiedByDelegate?.name ?? '',
      modifiedOn: node.writeDate ?? '',
      timeZoneRuleVersionNumber: node.timeZoneRuleVersionNumber ?? null
    },
    workInfo: {
      assistant: node.assistantId?.name ?? '',
      assistantId: node.assistantId?.id ?? '',
      companyName: node.companyName?.name ?? '',
      department: node.department ?? '',
      isAssistant: node.isAssistant ?? false,
      jobTitle: node.jobTitle ?? '',
      managingPartner: node.managingPartnerId?.name ?? '',
      managingPartnerId: node.managingPartnerId?.id ?? '',
      newCompanyName: node.newCompanyName ?? '',
      notAtCompanyFlag: node.notAtCompanyFlag ?? ''
    }
  };
}

// Choice options
export const emailStatusOptions = [
  { label: 'Bad', value: 'Bad' },
  { label: 'Good', value: 'Good' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Unknown', value: 'Unknown' }
];

export const estimatedProbabilityOptions = [
  { label: 'High', value: 'High' },
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Very High', value: 'Very High' }
];

export const genderOptions = [
  { label: 'Female', value: 'Female' },
  { label: 'Male', value: 'Male' },
  { label: 'Other', value: 'Other' },
  { label: 'Prefer not to say', value: 'Prefer not to say' }
];

export const likelyInvestmentAmountOptions = [
  { label: '$0 - $100K', value: '$0 - $100K' },
  { label: '$100K - $500K', value: '$100K - $500K' },
  { label: '$1M - $5M', value: '$1M - $5M' },
  { label: '$500K - $1M', value: '$500K - $1M' },
  { label: '$5M+', value: '$5M+' }
];

export const maritalStatusOptions = [
  { label: 'Divorced', value: 'Divorced' },
  { label: 'Married', value: 'Married' },
  { label: 'Separated', value: 'Separated' },
  { label: 'Single', value: 'Single' },
  { label: 'Widowed', value: 'Widowed' }
];

export const militaryAffiliationOptions = [
  { label: 'Air Force', value: 'Air Force' },
  { label: 'Army', value: 'Army' },
  { label: 'Coast Guard', value: 'Coast Guard' },
  { label: 'Marines', value: 'Marines' },
  { label: 'Navy', value: 'Navy' },
  { label: 'None', value: 'None' },
  { label: 'Space Force', value: 'Space Force' }
];

export const militaryDutyOptions = [
  { label: 'Active Duty', value: 'Active Duty' },
  { label: 'None', value: 'None' },
  { label: 'Reserve', value: 'Reserve' },
  { label: 'Retired', value: 'Retired' },
  { label: 'Veteran', value: 'Veteran' }
];

export const notAtCompanyFlagOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Left Company', value: 'Left Company' },
  { label: 'Unknown', value: 'Unknown' }
];

export const placementAgentOptions = [
  { label: 'External', value: 'External' },
  { label: 'Internal', value: 'Internal' },
  { label: 'None', value: 'None' }
];

export const relationshipTypeOptions = [
  { label: 'Advisor', value: 'Advisor' },
  { label: 'Investor', value: 'Investor' },
  { label: 'Other', value: 'Other' },
  { label: 'Partner', value: 'Partner' },
  { label: 'Prospect', value: 'Prospect' }
];

export const salutationOptions = [
  { label: 'Dr.', value: 'Dr.' },
  { label: 'Mr.', value: 'Mr.' },
  { label: 'Mrs.', value: 'Mrs.' },
  { label: 'Ms.', value: 'Ms.' },
  { label: 'Prof.', value: 'Prof.' }
];

export interface AllPotentialInvestorsData {
  potentialInvestors: {
    edges: PotentialInvestorEdge[];
  };
}

export interface PotentialInvestorEdge {
  node: PotentialInvestorNode;
}

export interface PotentialInvestorNode {
  id: string;
  isActive: boolean;
  name: string;
}
